import React, { useState } from 'react';
import '../styles/pages/Projects.css';
import projectsData from '../data/projectsData';

const Projects = () => {
  // Project data

  // State for filtering projects
  const [filter, setFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeMockup, setActiveMockup] = useState('web');

  // Filter projects based on category
  const filteredProjects = filter === 'all' 
    ? projectsData 
    : projectsData.filter(project => 
        Array.isArray(project.category) 
          ? project.category.includes(filter) 
          : project.category === filter
      );

  // Handle project click to show details
  const handleProjectClick = (project) => {
    setSelectedProject(project);
    if (filter === 'mobile') {
      setActiveMockup('app');
    } else if (filter === 'web') {
      setActiveMockup('web');
    } else {
      if (project.hasDualMockup) {
        setActiveMockup('web');
      } else {
        const isMobileOnly = Array.isArray(project.category) 
          ? project.category.includes('mobile') && !project.category.includes('web')
          : project.category === 'mobile';
        setActiveMockup(isMobileOnly ? 'app' : 'web');
      }
    }
  };

  // Close project details modal
  const closeModal = () => {
    setSelectedProject(null);
  };

  return (
    <div className="projects-page">
      <div className="container">
        <h1 className="section-title"><span className="highlight">My Projects</span></h1>
        
        {/* Filter buttons */}
        <div className="filter-container">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button 
            className={`filter-btn ${filter === 'web' ? 'active' : ''}`}
            onClick={() => setFilter('web')}
          >
            Web
          </button>
          <button 
            className={`filter-btn ${filter === 'mobile' ? 'active' : ''}`}
            onClick={() => setFilter('mobile')}
          >
            Mobile
          </button>
        </div>
        
        {/* Projects grid */}
        <div className="projects-grid">
          {filteredProjects.map((project) => (
            <div 
              key={project.id} 
              className="project-item"
              onClick={() => handleProjectClick(project)}
            >
              <div className="project-image">
                {project.image ? (
                  <img 
                    src={project.hasDualMockup && filter === 'mobile' ? project.appImage : project.image} 
                    alt={project.title} 
                  />
                ) : (
                  <div className="placeholder-image">
                    <i className="fas fa-laptop-code"></i>
                  </div>
                )}
              </div>
              <div className="project-info">
                <h3 className="project-title">
                  {project.title}
                </h3>
                <p className="project-description">{project.description}</p>
                <div className="project-tech">
                  {project.technologies.slice(0, 3).map((tech, index) => (
                    <span key={index}>{tech}</span>
                  ))}
                  {project.technologies.length > 3 && (
                    <span>+{project.technologies.length - 3}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Project details modal */}
        {selectedProject && (
          <div className="project-modal">
            <div className="modal-overlay" onClick={closeModal}></div>
            <div className="modal-content">
              <button className="close-btn" onClick={closeModal}>
                <i className="fas fa-times"></i>
              </button>
              
              <div className="modal-header">
                <h2>
                  {selectedProject.demoLink ? (
                    <a href={selectedProject.demoLink} target="_blank" rel="noopener noreferrer">
                      {selectedProject.title}
                    </a>
                  ) : (
                    selectedProject.title
                  )}
                </h2>
              </div>
              
              <div className="modal-body">
                <div className="modal-image-container">
                  {selectedProject.hasDualMockup ? (
                    <div className="dual-mockup-wrapper">
                      <div className="mockup-toggle">
                        <button 
                          className={`mockup-toggle-btn ${activeMockup === 'web' ? 'active' : ''}`}
                          onClick={() => setActiveMockup('web')}
                        >
                          <i className="fas fa-desktop"></i> Web View
                        </button>
                        <button 
                          className={`mockup-toggle-btn ${activeMockup === 'app' ? 'active' : ''}`}
                          onClick={() => setActiveMockup('app')}
                        >
                          <i className="fas fa-mobile-alt"></i> App View
                        </button>
                      </div>
                      
                      <div className={`dual-mockup-display ${selectedProject.category === 'mobile' ? 'mobile-first' : 'web-first'}`}>
                        <div className={`mockup-frame desktop-frame ${activeMockup === 'web' ? 'focus' : 'blur'}`} onClick={() => setActiveMockup('web')}>
                          <div className="frame-header">
                            <span className="dot red"></span>
                            <span className="dot yellow"></span>
                            <span className="dot green"></span>
                          </div>
                          <img src={selectedProject.webImage} alt="Web Mockup" />
                        </div>
                        
                        <div className={`mockup-frame mobile-frame ${activeMockup === 'app' ? 'focus' : 'blur'}`} onClick={() => setActiveMockup('app')}>
                          <div className="frame-speaker"></div>
                          <img src={selectedProject.appImage} alt="App Mockup" />
                          <div className="frame-home-button"></div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="modal-image">
                      {selectedProject.image ? (
                        <img src={selectedProject.image} alt={selectedProject.title} />
                      ) : (
                        <div className="placeholder-image large">
                          <i className="fas fa-laptop-code"></i>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="modal-description">
                  <h3>Project Overview</h3>
                  <p>{selectedProject.longDescription}</p>
                  
                  <h3>Technologies Used</h3>
                  <div className="modal-tech">
                    {selectedProject.technologies.map((tech, index) => (
                      <span key={index}>{tech}</span>
                    ))}
                  </div>
                  
                  <div className="modal-links">
                    {selectedProject.demoLink && (
                      <a href={selectedProject.demoLink} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                        <i className="fas fa-external-link-alt"></i> Live Demo
                      </a>
                    )}
                    {selectedProject.codeLink && (
                      <a href={selectedProject.codeLink} target="_blank" rel="noopener noreferrer" className="btn btn-outline">
                        <i className="fab fa-github"></i> Source Code
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;